import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Instagram, Facebook, Linkedin } from "lucide-react";
import { ROUTE_PATHS, CONTACT_INFO } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Accueil", path: ROUTE_PATHS.HOME },
    { name: "Nos Services", path: ROUTE_PATHS.SERVICES },
    { name: "À Propos", path: ROUTE_PATHS.ABOUT },
    { name: "Contact", path: ROUTE_PATHS.CONTACT },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Top Bar */}
      <div className="hidden lg:block bg-primary text-primary-foreground py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs font-medium">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" />
              {CONTACT_INFO.phone1} / {CONTACT_INFO.phone2}
            </span>
            <span className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              {CONTACT_INFO.email}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            {CONTACT_INFO.address}, {CONTACT_INFO.city}
          </div>
        </div>
      </div>

      {/* Header */}
      <header
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300 border-b",
          isScrolled 
            ? "bg-background/95 backdrop-blur-md border-border shadow-sm py-2"
            : "bg-background border-transparent py-4"
        )}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
            <img
              src={IMAGES.WHATSAPP_IMAGE_2026_02_18_AT_23_25_08_1_39}
              alt="YKE Multi_Services Logo"
              className="h-12 w-12 rounded-full border-2 border-primary/20 group-hover:border-primary transition-colors"
            />
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold tracking-tight text-foreground">
                YKE <span className="text-primary">Multi_Services</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                {CONTACT_INFO.slogan}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors hover:text-primary relative py-1",
                    isActive
                      ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary"
                      : "text-foreground/80"
                  )
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Button asChild variant="default" size="sm" className="rounded-full px-6 font-semibold">
              <Link to={ROUTE_PATHS.CONTACT}>Devis Gratuit</Link>
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-background md:hidden pt-24 pb-12 px-6 flex flex-col"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    cn(
                      "text-2xl font-semibold border-b border-border pb-4 flex justify-between items-center",
                      isActive ? "text-primary" : "text-foreground"
                    )
                  }
                >
                  {link.name}
                  <ChevronRight size={20} className="text-primary" />
                </NavLink>
              ))}
            </div>
            <div className="mt-auto space-y-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground uppercase tracking-widest">Contactez-nous</p>
                <a href={`tel:${CONTACT_INFO.phone1}`} className="flex items-center gap-3 text-lg font-medium">
                  <Phone className="text-primary" size={20} />
                  {CONTACT_INFO.phone1}
                </a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-lg font-medium">
                  <Mail className="text-primary" size={20} />
                  {CONTACT_INFO.email}
                </a>
              </div>
              <Button asChild className="w-full py-6 text-lg rounded-xl">
                <Link to={ROUTE_PATHS.CONTACT}>Demander un service</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="space-y-6">
              <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
                <img
                  src={IMAGES.WHATSAPP_IMAGE_2026_02_18_AT_23_25_08_1_39}
                  alt="YKE Logo"
                  className="h-12 w-12 rounded-full"
                />
                <span className="font-heading text-xl font-bold tracking-tight">
                  YKE <span className="text-primary">Multi_Services</span>
                </span>
              </Link>
              <p className="text-muted-foreground leading-relaxed">
                Votre partenaire de prestige en Côte d'Ivoire. Spécialiste en import de vins fins, résidences de luxe et développement foncier.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-all">
                  <Instagram size={18} />
                </a>
                <a href="#" className="p-2 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-all">
                  <Facebook size={18} />
                </a>
                <a href="#" className="p-2 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition-all">
                  <Linkedin size={18} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-1/2 after:h-0.5 after:bg-primary pb-2">
                Navigation
              </h4>
              <ul className="space-y-4">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group">
                      <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Column */}
            <div>
              <h4 className="text-lg font-bold mb-6 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-1/2 after:h-0.5 after:bg-primary pb-2">
                Nos Activités
              </h4>
              <ul className="space-y-4">
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-muted-foreground hover:text-primary transition-colors">
                    Vins & Champagnes d'Exception
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-muted-foreground hover:text-primary transition-colors">
                    Résidences Meublées Bassam
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-muted-foreground hover:text-primary transition-colors">
                    Assinie & Abidjan Séjours
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-muted-foreground hover:text-primary transition-colors">
                    Lotissements & Parcelles
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-lg font-bold mb-6 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-1/2 after:h-0.5 after:bg-primary pb-2">
                Contact
              </h4>
              <div className="space-y-4 text-muted-foreground">
                <div className="flex items-start gap-3">
                  <MapPin className="text-primary shrink-0 mt-1" size={18} />
                  <p>{CONTACT_INFO.address}, {CONTACT_INFO.city}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-primary shrink-0" size={18} />
                  <p>{CONTACT_INFO.phone1}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="text-primary shrink-0" size={18} />
                  <p className="break-all">{CONTACT_INFO.email}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© 2026 {CONTACT_INFO.companyName}. Tous droits réservés.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="hover:text-primary transition-colors">Mentions Légales</a>
              <a href="#" className="hover:text-primary transition-colors">Confidentialité</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
